import React, { useCallback, useId, useState } from "react";
import { Popover } from "@mui/material";
import { HexColorPicker } from "react-colorful";
import { STAGE_COLOR_PRESETS, normalizeStageColor } from "./colorUtils";
import "./NodeColorPicker.css";

/**
 * Compact color picker for node / board config surfaces. Preset swatches, a
 * saturation/hue panel, and hex input — styled to match the Node UI library.
 */
const NodeColorPicker = React.memo(function NodeColorPicker({
  label = "Color",
  value,
  onChange,
  presets = STAGE_COLOR_PRESETS,
  disabled = false,
  /** Square swatch only — no label or value text; opens the picker on click. */
  swatchOnly = false,
}) {
  const labelId = useId();
  const [anchor, setAnchor] = useState(null);
  const resolved = normalizeStageColor(value);
  const pickerColor = resolved || presets[0];
  const ariaLabel = label || "Color";

  const handleOpen = useCallback(
    (event) => {
      if (!disabled) setAnchor(event.currentTarget);
    },
    [disabled]
  );

  const handleClose = useCallback(() => setAnchor(null), []);

  const emit = useCallback(
    (next) => {
      onChange?.(normalizeStageColor(next));
    },
    [onChange]
  );

  const handlePick = useCallback(
    (hex) => {
      emit(hex);
    },
    [emit]
  );

  const handleHexInput = useCallback(
    (event) => {
      const next = normalizeStageColor(event.target.value);
      if (next) emit(next);
    },
    [emit]
  );

  const handleReset = useCallback(() => {
    onChange?.(null);
    handleClose();
  }, [onChange, handleClose]);

  return (
    <div
      className={`node-color-picker${swatchOnly ? " node-color-picker--swatch-only" : ""}`}
    >
      {!swatchOnly && label && (
        <span className="node-color-picker__label" id={labelId}>
          {label}
        </span>
      )}
      <button
        type="button"
        className="node-color-picker__trigger nodrag"
        onClick={handleOpen}
        disabled={disabled}
        aria-label={swatchOnly ? ariaLabel : undefined}
        aria-labelledby={!swatchOnly && label ? labelId : undefined}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
      >
        <span
          className={`node-color-picker__swatch${
            resolved ? "" : " node-color-picker__swatch--default"
          }`}
          style={resolved ? { background: resolved } : undefined}
        />
        {!swatchOnly && (
          <span className="node-color-picker__value">
            {resolved || "Default"}
          </span>
        )}
      </button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: "node-color-picker__panel",
            sx: {
              mt: 0.5,
              borderRadius: "8px",
              border: "1px solid var(--color-divider, #e2e8f0)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <div className="node-color-picker__presets">
          {presets.map((preset) => (
            <button
              key={preset}
              type="button"
              className={`node-color-picker__preset${
                resolved === preset ? " node-color-picker__preset--active" : ""
              }`}
              style={{ background: preset }}
              onClick={() => handlePick(preset)}
              aria-label={`Use ${preset}`}
            />
          ))}
        </div>

        <div className="node-color-picker__picker">
          <HexColorPicker color={pickerColor} onChange={handlePick} />
        </div>

        <div className="node-color-picker__hex">
          <span className="node-color-picker__hex-label">Hex</span>
          <input
            className="node-color-picker__hex-input nodrag"
            value={resolved || pickerColor}
            onChange={handleHexInput}
            spellCheck={false}
            aria-label="Hex color"
          />
        </div>

        {resolved && (
          <button type="button" className="node-color-picker__reset nodrag" onClick={handleReset}>
            Reset to default
          </button>
        )}
      </Popover>
    </div>
  );
});

export default NodeColorPicker;
